/**
 * ActionBar and StatusBar shells (design/ux/01_foundations.md §3.13).
 *
 * ActionBar — document and entry screens only: a sticky 56 px bar at the bottom
 * of the work area. Buttons show their keys, left to right, with the primary
 * verb last (lg). Every button is clickable. Under 1280 px the short labels
 * are used; under 768 px only the primary stays inline, the rest go into
 * « Plus… ».
 *
 * Generated from the registry (WP-17): pass `screen` (a SCREEN_SHORTCUTS id)
 * and the SAME `handlers` object given to `useShortcuts(screen, handlers)` —
 * the bar lists the screen's `bar` bindings that have a handler, with the
 * binding's key chip, and a click runs the handler the key runs, so labels,
 * chips and handlers cannot drift. `state` carries per-verb disabled /
 * loading / label overrides. Explicit `actions` remain for screens not yet
 * migrated to the registry.
 *
 * StatusBar — 28 px, 13 px text at the bottom of the shell (optional,
 * OP-F-20): connection / synchronisation state in words and with an icon, the
 * pharmacy, the user and role, the date. Hidden under 1024 px.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { Button, Icon } from "./primitives";
import { MenuButton } from "./Menu";
import { BREAKPOINTS } from "@/config/ui.config.js";
import { useMediaQuery } from "@/hooks/useMediaQuery.js";
import { SCREEN_SHORTCUTS } from "@/config/shortcuts.config.js";
import { barActions } from "@/lib/shortcut-dispatcher.js";

export interface ActionBarAction {
  id: string;
  label: string;
  shortLabel?: string;
  /** Key chip (« F9 », « Ctrl+S », « Échap »). */
  shortcut?: string;
  icon?: string;
  onClick?: () => unknown;
  primary?: boolean;
  disabled?: boolean;
  disabledReason?: React.ReactNode;
  loading?: boolean;
  danger?: boolean;
}

export interface ActionBarVerbState {
  disabled?: boolean;
  disabledReason?: React.ReactNode;
  loading?: boolean;
  label?: string;
  shortLabel?: string;
}

export interface ActionBarProps {
  /** Explicit actions (screens not yet on the registry). */
  actions?: ActionBarAction[];
  /** SCREEN_SHORTCUTS id: the bar is generated from the registry. */
  screen?: string;
  /** Explicit bindings (a dialog's own map) instead of `screen`. */
  bindings?: ReadonlyArray<any>;
  /** The handlers given to useShortcuts (same object). */
  handlers?: Record<string, unknown>;
  /** Per-verb state keyed by binding id. */
  state?: Record<string, ActionBarVerbState>;
  /** Accessible name (« Actions du document »). */
  label?: string;
  /** Content at the start of the bar (a status sentence, totals). */
  start?: React.ReactNode;
  className?: string;
}

export const ActionBar: React.FC<ActionBarProps> = ({
  actions: explicit,
  screen,
  bindings,
  handlers,
  state,
  label = "Actions du document",
  start,
  className,
}) => {
  const entries = bindings ?? (screen ? (SCREEN_SHORTCUTS as Record<string, any>)[screen]?.entries : null);
  const actions: ActionBarAction[] =
    explicit ?? (entries ? (barActions(entries, handlers as any, state as any) as ActionBarAction[]) : []);
  const phone = useMediaQuery(`(max-width: ${BREAKPOINTS.TABLET - 1}px)`);
  const primary = actions.filter((a) => a.primary);
  const others = actions.filter((a) => !a.primary);
  const renderButton = (a: ActionBarAction) => (
    <Button
      key={a.id}
      variant={a.primary ? "primary" : a.danger ? "danger-outline" : "secondary"}
      size={a.primary ? "lg" : "md"}
      icon={a.icon}
      shortcut={a.shortcut}
      disabled={a.disabled}
      disabledReason={a.disabledReason}
      loading={a.loading}
      onClick={() => a.onClick?.()}
      className="pk-actionbar-btn"
    >
      <span className="pk-actionbar-label">{a.label}</span>
      {a.shortLabel && <span className="pk-actionbar-short">{a.shortLabel}</span>}
    </Button>
  );
  return (
    <div role="group" aria-label={label} className={cn("pk-actionbar", className)}>
      {start && <div className="pk-actionbar-start">{start}</div>}
      <div className="pk-actionbar-actions">
        {phone && others.length > 0 ? (
          <MenuButton
            label="Plus…"
            size="md"
            align="end"
            items={others.map((a) => ({
              id: a.id,
              label: a.label,
              icon: a.icon,
              shortcut: a.shortcut,
              danger: a.danger,
              disabled: a.disabled || !!a.disabledReason,
              onSelect: () => a.onClick?.(),
            }))}
          />
        ) : (
          others.map(renderButton)
        )}
        {primary.map(renderButton)}
      </div>
    </div>
  );
};

export interface StatusBarItem {
  id: string;
  icon?: string;
  text: React.ReactNode;
  tone?: "neutral" | "warning" | "danger" | "success";
}

export const StatusBar: React.FC<{ items: StatusBarItem[]; className?: string }> = ({
  items,
  className,
}) => (
  <div className={cn("pk-statusbar", className)} role="status" aria-live="polite">
    {items.map((it) => (
      <span key={it.id} className={cn("pk-statusbar-item", it.tone && `pk-statusbar-item--${it.tone}`)}>
        {it.icon && <Icon name={it.icon} size={14} aria-hidden="true" />}
        {it.text}
      </span>
    ))}
  </div>
);
